import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  ArrowLeft, Phone, Copy, CheckCircle, Share2,
  GitBranch, ShoppingBag, Users, DollarSign, Gift
} from 'lucide-react';

interface ReferralProfile {
  id: number;
  phone: string;
  name?: string | null;
  referralCode: string;
  totalDirectRefs: number;
  totalIndirectRefs: number;
  totalNetworkSize: number;
  totalOrderValue: number;
  totalRewardEarned?: number;
  totalRewardPaid?: number;
  totalRewardPending?: number;
  tier: string;
  createdAt?: number;
}

interface DirectReferral {
  id: number;
  referrerPhone: string;
  refereePhone: string;
  refereeOrderId?: number;
  treeDepth: number;
  rootReferrerPhone?: string;
  status: string;
  createdAt: number;
  refereeName?: string;
  orderValue?: number;
}

interface IndirectNode {
  refereePhone: string;
  treeDepth: number;
  viaReferrer: string;
  refereeName?: string;
}

interface BroughtOrder {
  orderId: number;
  orderNum: string;
  refereeName?: string;
  orderValue: number;
  paymentConfirmed: number;
  createdAt: number;
}

interface RewardHistory {
  id: number;
  profileId: number;
  phone: string;
  sourceOrderId: number;
  sourceRecordId?: number;
  rewardType: string;
  amount: number;
  description?: string;
  status: string;
  approvedAt?: number;
  paidAt?: number;
  paidBy?: number;
  createdAt: number;
  sourceOrderNum?: string;
}

const TIER_STYLES: Record<string, string> = {
  bronze: 'bg-amber-100 text-amber-700',
  silver: 'bg-gray-100 text-gray-700',
  gold: 'bg-yellow-100 text-yellow-700',
  platinum: 'bg-purple-100 text-purple-700',
};

const TIER_LABELS: Record<string, string> = {
  bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum',
};

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

function formatDollars(dollars = 0): string {
  return `HK$${Math.round(dollars).toLocaleString('zh-HK')}`;
}
function formatCents(cents = 0): string {
  return `HK$${Math.round(cents / 100).toLocaleString('zh-HK')}`;
}

function formatDate(ts?: number): string {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('zh-HK');
}

type TabId = 'direct' | 'indirect' | 'orders' | 'rewards';

const TABS = [
  { id: 'direct' as TabId, label: '直接推薦', icon: Users },
  { id: 'indirect' as TabId, label: '間接網絡', icon: GitBranch },
  { id: 'orders' as TabId, label: '帶來的訂單', icon: ShoppingBag },
  { id: 'rewards' as TabId, label: '獎勵記錄', icon: Gift },
];

const AdminReferralDetail: React.FC = () => {
  const { phone } = useParams<{ phone: string }>();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<ReferralProfile | null>(null);
  const [direct, setDirect] = useState<DirectReferral[]>([]);
  const [indirect, setIndirect] = useState<IndirectNode[]>([]);
  const [orders, setOrders] = useState<BroughtOrder[]>([]);
  const [rewards, setRewards] = useState<RewardHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('direct');
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (!phone) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`/api/public/admin/referrals/${encodeURIComponent(phone)}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || '獲取詳情失敗');
        }
        const json = await res.json();
        if (cancelled) return;
        setProfile(json.profile || null);
        setDirect(Array.isArray(json.directReferrals) ? json.directReferrals : []);
        setIndirect(Array.isArray(json.indirectNetwork) ? json.indirectNetwork : []);
        setOrders(Array.isArray(json.broughtOrders) ? json.broughtOrders : []);
        setRewards(Array.isArray(json.rewardHistory) ? json.rewardHistory : []);
      } catch (e: any) {
        if (!cancelled) setError(e.message || '加載失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [phone]);

  useEffect(() => {
    if (profile?.referralCode && typeof window !== 'undefined') {
      setShareUrl(`${window.location.origin}/?gs=${encodeURIComponent(profile.referralCode)}`);
    }
  }, [profile?.referralCode]);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="referrals" onLogout={handleLogout}>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">加載中...</div>
      </AdminLayout>
    );
  }

  if (error || !profile) {
    return (
      <AdminLayout currentPage="referrals" onLogout={handleLogout}>
        <div className="text-center text-red-500 py-12">{error || '推薦人不存在'}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="referrals" onLogout={handleLogout}>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/referrals')}
          className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> 返回推薦網絡
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center text-orange-600 dark:text-orange-400 text-xl font-bold">
              {profile.name?.charAt(0) || '?'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{profile.name || '匿名'}</h1>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_STYLES[profile.tier?.toLowerCase()] || TIER_STYLES.bronze}`}>
                  {TIER_LABELS[profile.tier?.toLowerCase()] || profile.tier}
                </span>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {profile.phone}</span>
                <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> {profile.referralCode}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{shareUrl || '-'}</span>
              <button
                onClick={handleCopy}
                disabled={!shareUrl}
                className="text-xs flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-50"
              >
                {copied ? <CheckCircle className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                {copied ? '已複製' : '複製'}
              </button>
            </div>
            <button
              onClick={() => navigate(`/admin/referrals/${encodeURIComponent(profile.phone)}/tree`)}
              className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              <GitBranch className="w-4 h-4" /> 鏈路圖
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">直接推薦</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-600" />
            {profile.totalDirectRefs}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">間接推薦</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-blue-600" />
            {profile.totalIndirectRefs}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">網絡總人數</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-green-600" />
            {profile.totalNetworkSize}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">帶來訂單金額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-purple-600" />
            {formatDollars(profile.totalOrderValue)}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-2 px-2 md:space-x-6 md:px-4 overflow-x-auto" aria-label="分頁">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-1 transition ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-4">
          {activeTab === 'direct' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">被推薦人</th>
                    <th className="px-4 py-3">訂單號</th>
                    <th className="px-4 py-3 text-right">訂單金額</th>
                    <th className="px-4 py-3">時間</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {direct.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無直接推薦</td></tr>
                  ) : (
                    direct.map((d) => (
                      <tr key={d.id} className="hover:bg-orange-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800 dark:text-white">{d.refereeName || '匿名'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{d.refereePhone}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono">{d.refereeOrderId || '-'}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-white">{formatDollars(d.orderValue)}</td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(d.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'indirect' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">被推薦人</th>
                    <th className="px-4 py-3">鏈路深度</th>
                    <th className="px-4 py-3">經由誰</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {indirect.length === 0 ? (
                    <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無間接網絡</td></tr>
                  ) : (
                    indirect.map((node, idx) => (
                      <tr key={`${node.refereePhone}-${idx}`} className="hover:bg-orange-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800 dark:text-white">{node.refereeName || '匿名'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{node.refereePhone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{node.treeDepth}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-mono">{node.viaReferrer}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">訂單號</th>
                    <th className="px-4 py-3">被推薦人</th>
                    <th className="px-4 py-3 text-right">金額</th>
                    <th className="px-4 py-3">付款狀態</th>
                    <th className="px-4 py-3">時間</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {orders.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無帶來的訂單</td></tr>
                  ) : (
                    orders.map((o) => (
                      <tr key={o.orderId} className="hover:bg-orange-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 font-mono text-gray-800 dark:text-white">{o.orderNum}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800 dark:text-white">{o.refereeName || '匿名'}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-white">{formatDollars(o.orderValue)}</td>
                        <td className="px-4 py-3">
                          {o.paymentConfirmed ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">已付款</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">待付款</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(o.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'rewards' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">類型</th>
                    <th className="px-4 py-3 text-right">金額</th>
                    <th className="px-4 py-3">來源訂單</th>
                    <th className="px-4 py-3">狀態</th>
                    <th className="px-4 py-3">時間</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rewards.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無獎勵記錄</td></tr>
                  ) : (
                    rewards.map((r) => (
                      <tr key={r.id} className="hover:bg-orange-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">{r.rewardType}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-white">{formatCents(r.amount)}</td>
                        <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-300">{r.sourceOrderNum || r.sourceOrderId || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGES[r.status] || 'bg-gray-100 text-gray-700'}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminReferralDetail;
